import { useMutation } from '@apollo/client';
import { CREATE_ASSET } from '../../apollo/cms/GraphCMS/uploadAsset';
import { GRAPH_CMS_ASSET } from '../constants';
import { useSnackbar } from 'notistack';
import { ERROR_MESSAGES } from '../constants';
interface UploadAssetResponse {
  createAsset: {
    id: string;
    url: string;
    upload: {
      status:
        | 'ASSET_CREATE_PENDING'
        | 'ASSET_UPLOAD_COMPLETE'
        | 'ASSET_ERROR_UPLOAD';
      expiresAt: string;
      error?: {
        code: string;
        message: string;
      };
      requestPostData: {
        url: string;
        date: string;
        key: string;
        signature: string;
        algorithm: string;
        policy: string;
        credential: string;
        securityToken: string;
      };
    };
  };
}

interface UploadResult {
  id: string;
  url: string;
}

interface UseUploadAssetReturn {
  upload: (file: File) => Promise<UploadResult>;
}

export const useUploadAsset = (): UseUploadAssetReturn => {
  const { enqueueSnackbar } = useSnackbar();

  const [uploadAsset] = useMutation<UploadAssetResponse>(CREATE_ASSET, {
    context: {
      clientName: GRAPH_CMS_ASSET,
      uri: '/api/cms/assets/upload',
    },
  });

  const upload = async (file: File): Promise<UploadResult> => {
    try {
      // create asset and get upload URL
      const { data } = await uploadAsset({
        variables: {
          data: {
            fileName: file.name,
          },
        },
      });

      if (!data?.createAsset?.upload?.requestPostData) {
        throw new Error('Failed to get upload URL');
      }

      const expiresAt = new Date(data.createAsset.upload.expiresAt);
      if (expiresAt < new Date()) {
        throw new Error('Upload URL has expired');
      }

      // upload file using pre-signed URL
      const formData = new FormData();

      formData.append('key', data.createAsset.upload.requestPostData.key);
      formData.append(
        'X-Amz-Date',
        data.createAsset.upload.requestPostData.date
      );
      formData.append(
        'X-Amz-Signature',
        data.createAsset.upload.requestPostData.signature
      );
      formData.append(
        'X-Amz-Algorithm',
        data.createAsset.upload.requestPostData.algorithm
      );
      formData.append('policy', data.createAsset.upload.requestPostData.policy);
      formData.append(
        'X-Amz-Credential',
        data.createAsset.upload.requestPostData.credential
      );
      if (data.createAsset.upload.requestPostData.securityToken) {
        formData.append(
          'X-Amz-Security-Token',
          data.createAsset.upload.requestPostData.securityToken
        );
      }

      formData.append('file', file);

      const uploadResponse = await fetch(
        data.createAsset.upload.requestPostData.url,
        {
          method: 'POST',
          body: formData,
        }
      );

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload file');
      }

      return {
        id: data.createAsset.id,
        url: data.createAsset.url,
      };
    } catch (error) {
      console.error('Error uploading asset:', error);
      enqueueSnackbar(ERROR_MESSAGES.GENERIC_ERROR, {
        variant: 'error',
      });
      throw error;
    }
  };

  return { upload };
};
